/*
 * Letters bundle (LETTERS-BUNDLE 2026-10-03, LETTERS-BUNDLE-BUILDER 2026-10-06) - PDP logic for
 * snippets/letters-bundle.liquid.
 *
 * The text field is the single source of truth. Letters are typed; in "mix" mode a charm button
 * inserts the charm's symbol (♥) at the caret, so the field reads "AN♥NA". From the field this script
 * - builds the token list (letter or charm) and the sequence preview (<ol>, one <li> per token);
 *   in mix mode a token can be dragged to a new place, moved with the arrow keys or removed (×),
 *   which rewrites the field,
 * - blocks letters/charms that are not sold, sold out or short on stock (ANNA needs 2 x A),
 * - picks the tier variant from the number of tokens (smallest tier whose min-max fits) by
 *   "clicking" the hidden count option of the variant picker, so the theme updates price, URL and button,
 * - Pyramid parents (85727/85728, 7/10: plating x exact count 1-10): the customer picks the plating in
 *   the normal picker (it stays visible); only the count option is hidden. The tier is the variant with
 *   the selected plating AND min <= n <= max, and letters/charms/chain (stock per colour) come from that
 *   plating's set (data.sets, rendered from the variant metafields). Changing the plating switches the set,
 * - fills properties[_letters] ("AN[HEART]NA", read by the cart transform) and the visible property
 *   ("AN♥NA"),
 * - keeps the add-to-cart button disabled and stops the submit while the sequence is invalid.
 *
 * "charms" mode (8/10, charms only, e.g. the Sanya chain): no letters and no visible field - the charm
 * buttons append to the (hidden) field, the sequence can be reordered/removed as in mix, the count is the
 * number of charms, and the buttons are disabled at the largest count the selected plating sells.
 *
 * The checkout validation in the app timi-letters-bundle is the server-side guard; this script is
 * the customer-facing one. Class names are BEM (.letters-bundle__*), styling lives in
 * assets/letters-bundle.css.
 */
(function () {
  'use strict';

  var VARIATION_SELECTOR = /[︎️]/g;

  function symbolMap(data) {
    var map = {};
    Object.keys(data.charms || {}).forEach(function (key) {
      var symbol = (data.charms[key].symbol || '').replace(VARIATION_SELECTOR, '');
      if (symbol) map[symbol] = key;
    });
    return map;
  }

  // "an♥na" -> [{type:'letter',key:'A'}, ..., {type:'charm',key:'HEART'}, ...]
  function tokenize(value, data) {
    var symbols = symbolMap(data);
    var tokens = [];
    Array.from((value || '').replace(VARIATION_SELECTOR, '')).forEach(function (c) {
      if (/\s/.test(c)) return;
      if (symbols[c]) {
        tokens.push({ type: 'charm', key: symbols[c] });
      } else {
        tokens.push({ type: 'letter', key: c.toUpperCase() });
      }
    });
    return tokens;
  }

  function display(token, data) {
    return token.type === 'charm' ? data.charms[token.key].symbol : token.key;
  }

  function label(token, data) {
    return token.type === 'charm' ? data.charms[token.key].label : token.key;
  }

  // Value read by the function: letters as they are, charms as [KEY].
  function serialize(tokens) {
    return tokens.map(function (t) { return t.type === 'charm' ? '[' + t.key + ']' : t.key; }).join('');
  }

  function normalize(value) {
    return (value || '').toUpperCase().replace(/\s+/g, '');
  }

  // Which option of the parent is the count (0-based). One option = that one (old parents: 1 / 2-3 /
  // 4-10 letters). Several = the option whose values all hold a number (Pyramid: "1".."10"), else the
  // one whose values differ between variants of the same plating.
  function countOptionIndex(tiers) {
    var size = 0;
    tiers.forEach(function (t) {
      (t.options || []).forEach(function (v, i) { if (v != null && v !== '') size = Math.max(size, i + 1); });
    });
    if (size <= 1) return 0;
    var numeric = [];
    for (var i = 0; i < size; i++) {
      var all = tiers.every(function (t) { return /\d/.test(String((t.options || [])[i] || '')); });
      if (all) numeric.push(i);
    }
    if (numeric.length === 1) return numeric[0];
    for (var j = 0; j < size; j++) {
      var seen = {};
      for (var k = 0; k < tiers.length; k++) {
        var t = tiers[k];
        var plating = t.plating || '';
        var value = (t.options || [])[j];
        if (seen[plating] !== undefined && seen[plating] !== value) return j;
        seen[plating] = value;
      }
    }
    return size - 1;
  }

  // The data check() works on for the current choice: the tiers whose other options (plating) equal
  // the selection, and the letter set of their plating. selection = option values by index (the count
  // index is ignored); null/[] = no picker -> the tiers of the given variant id, else all tiers.
  function view(data, selection, variantId) {
    var countIndex = countOptionIndex(data.tiers || []);
    var tiers = data.tiers || [];
    var pick = selection && selection.length ? selection : null;
    if (!pick && variantId != null) {
      var current = tiers.filter(function (t) { return String(t.id) === String(variantId); })[0];
      if (current) pick = current.options;
    }
    if (pick) {
      tiers = tiers.filter(function (t) {
        return (t.options || []).every(function (v, i) {
          return i === countIndex || v == null || pick[i] == null || v === pick[i];
        });
      });
    }
    var plating = tiers.length ? tiers[0].plating || '' : '';
    var sets = data.sets || {};
    var set = sets[plating] || sets[''] || { configured: false, chainAvailable: false, letters: {}, charms: {} };
    return {
      max: data.max,
      mode: data.mode,
      plating: plating,
      countIndex: countIndex,
      configured: !!set.configured,
      chainAvailable: !!set.chainAvailable,
      letters: set.letters || {},
      charms: set.charms || {},
      tiers: tiers
    };
  }

  // Pure check, mirrored by extensions/letters-bundle/src/letters.js in the app. data = view(...).
  function check(value, data) {
    var tokens = tokenize(value, data);
    var result = {
      tokens: tokens,
      text: tokens.map(function (t) { return display(t, data); }).join(''),
      letters: serialize(tokens),
      tier: null,
      error: null,
      detail: ''
    };
    if (!data.configured || !data.chainAvailable) {
      result.error = 'unavailable';
      return result;
    }
    if (tokens.length === 0) {
      result.error = 'empty';
      return result;
    }
    if (tokens.length > data.max) {
      result.error = 'too_many';
      return result;
    }

    var unknown = [];
    var counts = {};
    tokens.forEach(function (t) {
      var id = t.type + ':' + t.key;
      var known = t.type === 'charm' ? data.charms[t.key] : data.letters[t.key];
      if (!known) {
        if (unknown.indexOf(t.key) === -1) unknown.push(t.key);
        return;
      }
      counts[id] = (counts[id] || 0) + 1;
    });
    if (unknown.length) {
      result.error = 'unknown';
      result.detail = unknown.join(', ');
      return result;
    }

    var soldOut = [];
    Object.keys(counts).forEach(function (id) {
      var parts = id.split(':');
      var entry = parts[0] === 'charm' ? data.charms[parts[1]] : data.letters[parts[1]];
      if (!entry.available || (entry.stock !== null && entry.stock < counts[id])) {
        soldOut.push(parts[0] === 'charm' ? entry.symbol : parts[1]);
      }
    });
    if (soldOut.length) {
      result.error = 'sold_out';
      result.detail = soldOut.join(', ');
      return result;
    }

    var n = tokens.length;
    var tiers = data.tiers
      .filter(function (t) { return t.available && (t.min || 1) <= n && t.max >= n; })
      .sort(function (a, b) { return a.max - b.max; });
    if (!tiers.length) {
      result.error = 'unavailable';
      return result;
    }
    result.tier = tiers[0];
    return result;
  }

  function selectTier(container, form, tier, countIndex) {
    var index = 'option' + (countIndex + 1);
    var value = tier.options[countIndex];
    var inputs = container.querySelectorAll('[data-variant-input][data-index="' + index + '"]');
    for (var i = 0; i < inputs.length; i++) {
      var input = inputs[i];
      if (input.getAttribute('type') === 'radio') {
        if (input.value === value) {
          if (!input.checked) {
            input.checked = true;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
          return;
        }
      } else if (input.tagName === 'SELECT') {
        if (input.value !== value) {
          input.value = value;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
        return;
      }
    }
    // No picker in the template: set the master select directly (price text is not updated).
    var master = form && form.querySelector('select[name="id"]');
    if (master) master.value = String(tier.id);
  }

  // The values the customer has selected in the visible picker, by option index (null = none).
  function readSelection(container, size) {
    var selection = [];
    for (var i = 0; i < size; i++) {
      var value = null;
      var inputs = container.querySelectorAll('[data-variant-input][data-index="option' + (i + 1) + '"]');
      for (var j = 0; j < inputs.length; j++) {
        var input = inputs[j];
        if (input.tagName === 'SELECT') { value = input.value; break; }
        if (input.checked) { value = input.value; break; }
      }
      selection.push(value);
    }
    return selection.some(function (v) { return v !== null; }) ? selection : null;
  }

  function init(root) {
    if (root.dataset.lettersReady) return;
    root.dataset.lettersReady = 'true';

    var raw;
    try {
      raw = JSON.parse(root.querySelector('[data-letters-bundle-data]').textContent);
    } catch (e) {
      return;
    }
    raw.tiers = raw.tiers || [];
    var mix = raw.mode === 'mix';
    var charmsOnly = raw.mode === 'charms';
    var withCharms = mix || charmsOnly;

    var form = root.closest('form');
    var container = root.closest('[id^="ProductSection-"]') || form || document;
    var input = root.querySelector('[data-letters-input]');
    var sequence = root.querySelector('[data-letters-sequence]');
    var counter = root.querySelector('[data-letters-counter]');
    var errorEl = root.querySelector('[data-letters-error]');
    var hidden = root.querySelector('[data-letters-hidden]');
    var visible = root.querySelector('[data-letters-visible]');
    var charmGroup = root.querySelector('[data-letters-charms]');
    var button = form && form.querySelector('[data-add-to-cart]');
    var touched = false;
    var dragFrom = null;
    var optionCount = raw.tiers.reduce(function (n, t) { return Math.max(n, (t.options || []).filter(function (v) { return v != null; }).length); }, 0);
    var countIndex = countOptionIndex(raw.tiers);
    var master = form && form.querySelector('select[name="id"]');

    function currentView() {
      return view(raw, readSelection(container, optionCount), master && master.value ? master.value : raw.currentVariant);
    }

    var data = currentView();
    var state = check('', data);

    // Only the count option is hidden; the plating stays a normal choice. The CSS hides all wrappers
    // until data-count-option is set, so the count never flashes.
    root.setAttribute('data-count-option', 'option' + (countIndex + 1));
    container.querySelectorAll('.variant-wrapper').forEach(function (el) {
      if (el.querySelector('[data-index="option' + (countIndex + 1) + '"]')) el.hidden = true;
    });

    // Charms only: the largest count the selected plating sells (its available tiers), else data.max.
    function charmLimit() {
      var limit = 0;
      data.tiers.forEach(function (t) { if (t.available) limit = Math.max(limit, t.max); });
      return Math.min(limit || data.max, data.max);
    }

    function message(result) {
      switch (result.error) {
        case 'empty': return root.dataset.textEmpty;
        case 'too_many': return root.dataset.textTooMany;
        case 'unknown': return root.dataset.textUnknown.replace('[letters]', result.detail);
        case 'sold_out': return root.dataset.textSoldOut.replace('[letters]', result.detail);
        default: return root.dataset.textUnavailable;
      }
    }

    function applyButton() {
      if (!button) return;
      if (state.error) {
        button.setAttribute('disabled', 'disabled');
      } else if (state.tier && state.tier.available) {
        button.removeAttribute('disabled');
      }
    }

    // Rewrites the field from a token list (after drag, move or remove) and re-renders.
    function setTokens(tokens, focusIndex) {
      input.value = tokens.map(function (t) { return display(t, data); }).join('');
      render();
      if (focusIndex != null) {
        var item = sequence.children[Math.max(0, Math.min(focusIndex, sequence.children.length - 1))];
        if (item) item.focus();
      }
    }

    function move(from, to) {
      var tokens = state.tokens.slice();
      if (from === to || to < 0 || to >= tokens.length) return;
      var token = tokens.splice(from, 1)[0];
      tokens.splice(to, 0, token);
      setTokens(tokens, to);
    }

    function remove(index) {
      var tokens = state.tokens.slice();
      tokens.splice(index, 1);
      setTokens(tokens, index);
    }

    function renderSequence() {
      sequence.textContent = '';
      state.tokens.forEach(function (token, index) {
        var item = document.createElement('li');
        item.className = 'letters-bundle__token letters-bundle__token--' + token.type;
        item.dataset.index = String(index);
        var text = document.createElement('span');
        text.className = 'letters-bundle__token-text';
        text.textContent = display(token, data);
        item.appendChild(text);
        if (withCharms) {
          item.draggable = true;
          item.tabIndex = 0;
          var removeButton = document.createElement('button');
          removeButton.type = 'button';
          removeButton.className = 'letters-bundle__token-remove';
          removeButton.setAttribute('aria-label', root.dataset.textRemove.replace('[item]', label(token, data)));
          removeButton.textContent = '×';
          removeButton.addEventListener('click', function () { remove(index); });
          item.appendChild(removeButton);
        }
        sequence.appendChild(item);
      });
    }

    function render() {
      state = check(input.value, data);
      if (input.value !== state.text && state.error !== 'unavailable') {
        var caret = input.selectionStart;
        input.value = state.text;
        if (caret != null && document.activeElement === input) input.setSelectionRange(caret, caret);
      }

      renderSequence();
      counter.textContent = charmsOnly
        ? String(state.tokens.length) + '/' + String(charmLimit())
        : root.dataset.textCounter
          .replace('[current]', String(state.tokens.length))
          .replace('[max]', String(data.max));
      updateCharmButtons();

      var showError = state.error && (touched || state.error !== 'empty');
      errorEl.hidden = !showError;
      errorEl.textContent = showError ? message(state) : '';
      input.setAttribute('aria-invalid', state.error && state.error !== 'empty' ? 'true' : 'false');

      if (state.error) {
        hidden.disabled = true;
        visible.disabled = true;
        hidden.value = '';
        visible.value = '';
      } else {
        selectTier(container, form, state.tier, countIndex);
        hidden.disabled = false;
        visible.disabled = false;
        hidden.value = state.letters;
        visible.value = state.text;
      }
      applyButton();
    }

    // Charms only: a button is also disabled once the sequence holds the largest count on sale.
    function updateCharmButtons() {
      if (!withCharms || !charmGroup) return;
      var full = charmsOnly && state.tokens.length >= charmLimit();
      charmGroup.querySelectorAll('.letters-bundle__charm').forEach(function (el) {
        var charm = data.charms[el.dataset.charm];
        el.disabled = !charm || !charm.available || !data.configured || full;
      });
    }

    // Charm buttons (mix and charms mode): inserted at the caret (charms only: appended), one place
    // each. Rebuilt when the plating changes (stock per colour).
    function renderCharms() {
      if (!withCharms || !charmGroup) return;
      charmGroup.querySelectorAll('.letters-bundle__charm').forEach(function (el) { el.remove(); });
      Object.keys(data.charms).forEach(function (key) {
        var charm = data.charms[key];
        var charmButton = document.createElement('button');
        charmButton.type = 'button';
        charmButton.className = 'letters-bundle__charm';
        charmButton.dataset.charm = key;
        charmButton.setAttribute('aria-label', root.dataset.textAdd.replace('[item]', charm.label));
        charmButton.disabled = !charm.available || !data.configured;
        var symbol = document.createElement('span');
        symbol.className = 'letters-bundle__charm-symbol';
        symbol.setAttribute('aria-hidden', 'true');
        symbol.textContent = charm.symbol;
        var text = document.createElement('span');
        text.className = 'letters-bundle__charm-label';
        text.textContent = charm.label;
        charmButton.appendChild(symbol);
        charmButton.appendChild(text);
        charmButton.addEventListener('click', function () {
          if (charmsOnly) {
            input.value += charm.symbol;
            touched = true;
            render();
            return;
          }
          var start = input.selectionStart != null ? input.selectionStart : input.value.length;
          var end = input.selectionEnd != null ? input.selectionEnd : start;
          input.value = input.value.slice(0, start) + charm.symbol + input.value.slice(end);
          touched = true;
          render();
          var caret = Math.min(start + charm.symbol.length, input.value.length);
          input.focus();
          input.setSelectionRange(caret, caret);
        });
        charmGroup.appendChild(charmButton);
      });
      updateCharmButtons();
    }
    renderCharms();

    if (withCharms && charmGroup) {
      sequence.addEventListener('dragstart', function (evt) {
        var item = evt.target.closest('.letters-bundle__token');
        if (!item) return;
        dragFrom = Number(item.dataset.index);
        item.classList.add('letters-bundle__token--dragging');
        if (evt.dataTransfer) {
          evt.dataTransfer.effectAllowed = 'move';
          evt.dataTransfer.setData('text/plain', item.dataset.index);
        }
      });
      sequence.addEventListener('dragover', function (evt) {
        if (dragFrom !== null) evt.preventDefault();
      });
      sequence.addEventListener('drop', function (evt) {
        var item = evt.target.closest('.letters-bundle__token');
        if (dragFrom === null || !item) return;
        evt.preventDefault();
        var to = Number(item.dataset.index);
        var from = dragFrom;
        dragFrom = null;
        move(from, to);
      });
      sequence.addEventListener('dragend', function () {
        dragFrom = null;
        sequence.querySelectorAll('.letters-bundle__token--dragging').forEach(function (el) {
          el.classList.remove('letters-bundle__token--dragging');
        });
      });
      sequence.addEventListener('keydown', function (evt) {
        var item = evt.target.closest('.letters-bundle__token');
        if (!item || evt.target !== item) return;
        var index = Number(item.dataset.index);
        if (evt.key === 'ArrowLeft' || evt.key === 'ArrowUp') {
          evt.preventDefault();
          move(index, index - 1);
        } else if (evt.key === 'ArrowRight' || evt.key === 'ArrowDown') {
          evt.preventDefault();
          move(index, index + 1);
        } else if (evt.key === 'Delete' || evt.key === 'Backspace') {
          evt.preventDefault();
          remove(index);
        }
      });
    }

    input.addEventListener('input', render);
    input.addEventListener('blur', function () {
      touched = true;
      render();
    });

    // The theme re-enables the button on every variant change; keep it in line with the sequence.
    // A new plating switches the letter set (stock per colour) and re-picks the count.
    container.addEventListener('variantChange', function () {
      window.setTimeout(function () {
        var next = currentView();
        if (next.plating !== data.plating) {
          data = next;
          renderCharms();
          render();
        } else {
          data = next;
          applyButton();
        }
      }, 0);
    });

    if (form) {
      form.addEventListener('submit', function (evt) {
        render();
        if (state.error) {
          touched = true;
          render();
          evt.preventDefault();
          evt.stopImmediatePropagation();
          var first = charmsOnly && charmGroup ? charmGroup.querySelector('.letters-bundle__charm:not(:disabled)') : input;
          if (first) first.focus();
        }
      }, true);
    }

    render();
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-letters-bundle]').forEach(init);
  }

  // Exposed for the theme editor / quick view and for tests.
  window.TimiLettersBundle = {
    check: check, view: view, countOptionIndex: countOptionIndex,
    tokenize: tokenize, serialize: serialize, normalize: normalize, initAll: initAll
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', function (evt) { initAll(evt.target); });
  document.addEventListener('quickview:loaded', function () { initAll(); });
})();
